import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { practiceSettingsPath } from "../../../../shared/navigation/practiceSettings";
import { Loader2 } from "lucide-react";

import { useConnectIntegration } from "../../application/useScheduler";
import {
    consumeExpectedState,
    isIntegrationProvider,
    redirectUriFor,
} from "../../application/oauth";

type Status = "working" | "error";

/**
 * Microsoft's refusal when the organisation lets only an admin approve apps
 * (AADSTS90094) or the grant needs consent nobody gave yet (AADSTS65001).
 * The therapist did not say no, so "you did not grant access" would be wrong.
 */
function needsAdminConsent(description: string | null): boolean {
    return /AADSTS(90094|65001)/.test(description ?? "");
}

/**
 * Landing page for the provider redirect.
 *
 * Trades the authorization code for stored credentials, then returns to the
 * settings page. The `state` is checked first: without it a forged redirect
 * could connect an attacker's calendar to this therapist's account.
 */
export default function SchedulerOAuthCallbackPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { provider } = useParams();
    const [searchParams] = useSearchParams();
    const connect = useConnectIntegration();

    const [status, setStatus] = useState<Status>("working");
    const [reason, setReason] = useState<string | null>(null);
    // Providers re-deliver the same code on a refresh; exchanging twice fails.
    const exchanged = useRef(false);

    useEffect(() => {
        if (exchanged.current) return;
        exchanged.current = true;

        if (!isIntegrationProvider(provider)) {
            setStatus("error");
            setReason(t("scheduler.oauth.unknownProvider"));
            return;
        }

        const denied = searchParams.get("error");
        if (denied) {
            setStatus("error");
            setReason(
                needsAdminConsent(searchParams.get("error_description"))
                    ? t("scheduler.oauth.adminConsentRequired")
                    : t("scheduler.oauth.denied")
            );
            return;
        }

        const code = searchParams.get("code");
        if (!code) {
            setStatus("error");
            setReason(t("scheduler.oauth.missingCode"));
            return;
        }

        if (!consumeExpectedState(provider, searchParams.get("state"))) {
            setStatus("error");
            setReason(t("scheduler.oauth.stateMismatch"));
            return;
        }

        connect.mutate(
            { provider, code, redirectUri: redirectUriFor(provider) },
            {
                onSuccess: () =>
                    navigate(practiceSettingsPath("scheduler", { connected: provider }), {
                        replace: true,
                    }),
                onError: () => {
                    setStatus("error");
                    setReason(t("scheduler.oauth.exchangeFailed"));
                },
            }
        );
        // Runs once: the code is single-use, so re-running would fail anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
            {status === "working" ? (
                <>
                    <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                    <p role="status" className="text-sm text-gray-500">
                        {t("scheduler.oauth.connecting")}
                    </p>
                </>
            ) : (
                <>
                    <p role="alert" className="text-sm text-red-600">
                        {reason}
                    </p>
                    <Link
                        to={practiceSettingsPath("scheduler")}
                        className="text-sm text-brand-600 underline"
                    >
                        {t("scheduler.oauth.backToSettings")}
                    </Link>
                </>
            )}
        </div>
    );
}
